import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import CustomChip from '../../../shared/CustomChip';
import moment from 'moment';
import CustomTable from '../../../shared/CustomTable';
import CustomPagination from '../../../shared/CustomPagination';
import Button from '../../../shared/CustomButton';
import DatePicker from '../../../shared/DatePicker';
import TextInput from '../../../shared/TextInput';
import DropdownField from '../../../shared/DropdownField';
import SearchField from '../../../shared/SearchField';
import ReusableModal from '../../../shared/ReuseableModal';
import TicketDetail from './TicketDetail';
import { 
  FaRedo, 
  FaExclamationTriangle,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaTicketAlt,
  FaFilter
} from 'react-icons/fa';
import { 
  helpDeskDepartments, 
  ticketPriorities, 
  ticketStatuses, 
  dummyTickets,
  ticketColumns 
} from '../../../constants/QueueManagementConstants';
import { EditIcon, DeleteIcon, EyeIcon } from '../../../utils/TableActionIcons';

export default function TicketManagementTab() {
  const { t } = useTranslation();
  const getAllTickets = () => {
    const local = JSON.parse(localStorage.getItem('tickets') || '[]'); 
    let maxId = [...dummyTickets, ...local].reduce((m, { id }) => (id && id > m ? id : m), 0);
    let dirty = false;
    for (const t of local) {
      if (!t.id) {
        t.id = ++maxId;
        dirty = true;
      }
    }
    if (dirty) localStorage.setItem('tickets', JSON.stringify(local));

    return [...local, ...dummyTickets];
  };

  const [tickets, setTickets] = useState(getAllTickets());

  React.useEffect(() => {
    const refresh = () => setTickets(getAllTickets());
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);
  
  const [filteredTickets, setFilteredTickets] = useState(tickets);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  
  const [filters, setFilters] = useState({
    search: '',
    department: '',
    priority: '',
    status: '',
    assignee: '',
    dateRange: null 
  });

  const [bulkActions, setBulkActions] = useState({
    selectedTickets: [],
    action: ''
  });

  // Apply filters
  React.useEffect(() => {
    let filtered = [...tickets];
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.subject.toLowerCase().includes(searchLower) ||
        ticket.ticketId.toLowerCase().includes(searchLower) ||
        ticket.requester.toLowerCase().includes(searchLower) ||
        ticket.description.toLowerCase().includes(searchLower)
      );
    }
    
    if (filters.department) {
      filtered = filtered.filter(ticket => ticket.department === filters.department);
    }
    
    if (filters.priority) {
      filtered = filtered.filter(ticket => ticket.priority === filters.priority);
    }
    
    if (filters.status) {
      filtered = filtered.filter(ticket => ticket.status === filters.status);
    }
    
    if (filters.assignee) {
      filtered = filtered.filter(ticket => 
        ticket.assignee.toLowerCase().includes(filters.assignee.toLowerCase())
      );
    }
    
    if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      if (startDate && endDate) {
        filtered = filtered.filter(ticket => {
          const ticketDate = moment(ticket.createdDate, 'YYYY-MM-DD').toDate();
          const start = moment(startDate, 'YYYY-MM-DD').toDate();
          const end = moment(endDate, 'YYYY-MM-DD').toDate();
          return ticketDate >= start && ticketDate <= end;
        });
      }
    }
    
    setFilteredTickets(filtered);
    setCurrentPage(1);
  }, [filters, tickets]);

  const handleFilterChange = (name, value) => {
    if (name === 'dateRange') {
      if (value && Array.isArray(value) && value.length === 2) {
        const formattedDates = value.map(date => 
          date ? moment(date).format('YYYY-MM-DD') : null
        ).filter(Boolean);
        
        if (formattedDates.length === 2) {
          setFilters(prev => ({
            ...prev,
            [name]: formattedDates
          }));
        }
      } else if (value === null || value === undefined) {
        setFilters(prev => ({
          ...prev,
          [name]: null
        }));
      }
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      department: '',
      priority: '',
      status: '',
      assignee: '',
      dateRange: null 
    });
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsDetailModalOpen(true);
  };

  const handleUpdateTicket = (updatedTicket) => {
    setTickets(prev => prev.map(ticket => 
      ticket.id === updatedTicket.id ? updatedTicket : ticket
    ));
    setSelectedTicket(updatedTicket);
  };

  const handleDeleteTicket = (idOrTicketId) => {
    const ticketToDelete = tickets.find(t => t.id === idOrTicketId || t.ticketId === idOrTicketId);
    if (!ticketToDelete) return;
    if (window.confirm(t('Are you sure you want to delete this ticket?'))) {
      setTickets(prev => prev.filter(ticket => ticket.id !== ticketToDelete.id));
      const localTickets = JSON.parse(localStorage.getItem('tickets') || '[]');
      const updatedLocal = localTickets.filter(ticket =>
        ticket.id !== ticketToDelete.id && ticket.ticketId !== ticketToDelete.ticketId);
      localStorage.setItem('tickets', JSON.stringify(updatedLocal));
      if (selectedTicket && selectedTicket.id === ticketToDelete.id) {
        setIsDetailModalOpen(false);
        setSelectedTicket(null);
      }
      toast.success(t('Ticket deleted successfully!'));
    }
  };
  const priorityTemplate = (rowData) => {
    let chipType = 'default';
    if (rowData.priority === 'Critical') chipType = 'danger';
    else if (rowData.priority === 'High') chipType = 'warning';
    else if (rowData.priority === 'Medium') chipType = 'success';
    else if (rowData.priority === 'Low') chipType = 'secondary';
    else chipType = 'default';
    return (
      <CustomChip
        label={rowData.priority}
        chipType={chipType}
        className="capitalize"
      />
    );
  };
  const statusTemplate = (rowData) => {
    const statusIcons = {
      'Open': <FaExclamationTriangle className="mr-1" />,
      'InProgress': <FaClock className="mr-1" />,
      'Resolved': <FaCheckCircle className="mr-1" />,
      'Closed': <FaTimesCircle className="mr-1" />,
      'OnHold': <FaClock className="mr-1" />,
      'Pending': <FaClock className="mr-1" />,
      'Completed': <FaCheckCircle className="mr-1" />,
      'Cancelled': <FaTimesCircle className="mr-1" />,
    };
    const statusChipTypes = {
      'Open': 'blue',
      'InProgress': 'gradient',
      'Resolved': 'success',
      'Closed': 'secondary',
      'OnHold': 'warning',
      'Pending': 'warning',
      'Completed': 'success',
      'Cancelled': 'danger',
    };
    const chipType = statusChipTypes[rowData.status] || 'default';

    return (
      <CustomChip
        label={rowData.status.replace(/([A-Z])/g, ' $1').trim()}
        chipType={chipType}
        icon={statusIcons[rowData.status]}
        className="capitalize"
      />
    );
  };

  // Actions Template
  const ActionsTemplate = rowData => (
    <div className="flex gap-1 justify-center items-center">
      <EyeIcon
        onClick={() => handleViewTicket(rowData)}
        title={t('View Details')}
      />
      <EditIcon
        onClick={() => handleViewTicket(rowData)}
        title={t('Edit Ticket')}
      />
      <DeleteIcon
        onClick={() => handleDeleteTicket(rowData.id)}
        title={t('Delete Ticket')}
      />
    </div>
  );

  const dateTemplate = (rowData, field) => {
    return new Date(rowData[field]).toLocaleString();
  };
  const enhancedColumns = [
    {
      field: 'actions',
      header: t('Actions'),
      body: ActionsTemplate,
      sortable: false,
      headerCellClass: 'text-center',
      bodyCellClass: 'text-center'
    },
    {
      field: 'select',
      header: '',
      body: (rowData) => (
        <input
          type="checkbox"
          checked={bulkActions.selectedTickets.includes(rowData.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setBulkActions(prev => ({
                ...prev,
                selectedTickets: [...prev.selectedTickets, rowData.id]
              }));
            } else {
              setBulkActions(prev => ({
                ...prev,
                selectedTickets: prev.selectedTickets.filter(id => id !== rowData.id)
              }));
            }
          }}
        />
      ),
      headerCellClass: 'text-center w-12',
      bodyCellClass: 'text-center'
    },
    ...ticketColumns.filter(col => col.field !== 'actions').map(col => ({
      ...col,
      header: t(col.header),
      body: col.field === 'priority' ? priorityTemplate :
            col.field === 'status' ? statusTemplate :
            col.field === 'createdDate' || col.field === 'lastUpdated' ? 
            (rowData) => dateTemplate(rowData, col.field) : undefined
    })),
  ];

  // Pagination
  const totalRecords = filteredTickets.length;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedTickets = filteredTickets.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div>
      <div className="">
        {/* Header */}
        {/* <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <FaTicketAlt className="text-2xl text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {t("Ticket Management")}
                </h1>
                <p className="text-gray-600 text-sm mt-1">
                  {t("Track, manage and resolve support tickets")}
                </p>
              </div>
            </div>
             */}
            {/* Bulk Actions */}
            {/* {bulkActions.selectedTickets.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {bulkActions.selectedTickets.length} {t('selected')}
                </span>
                <DropdownField
                  useFormik={false}
                  value={bulkActions.action}
                  handleChange={(e) => setBulkActions(prev => ({ ...prev, action: e.value }))}
                  options={[
                    { label: t('Select Action'), value: '' },
                    { label: t('Close Tickets'), value: 'close' },
                    { label: t('Delete Tickets'), value: 'delete' }
                  ]}
                  placeholder={t('Bulk Actions')}
                />
                <Button
                  onClick={handleBulkAction}
                  label={t('Apply')}
                  buttonType="primary"
                  disabled={!bulkActions.action}
                />
              </div>
            )}
          </div>
        </div> */}

        {/* Filters Section */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-6">
            <FaFilter className="text-gray-600" />
            <h3 className="font-semibold text-gray-900">{t('Filter Tickets')}</h3>
          </div>
          
          {/* Main Filters Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Search */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {t("Search Tickets")}
              </label>
              <SearchField
                placeholder={t("Search...")}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full"
              />
            </div>

            {/* Department Filter */}
            <div className="space-y-2">
              <DropdownField
                useFormik={false}
                label={t("Department")}
                value={filters.department}
                handleChange={(e) => handleFilterChange('department', e.value)}
                options={[{ value: '', label: t('All Departments') }, ...helpDeskDepartments]}
                placeholder={t("Select Department")}
                className="w-full"
              />
            </div>

            {/* Priority Filter */}
            <div className="space-y-2">
              <DropdownField
                useFormik={false}
                label={t("Priority")}
                value={filters.priority}
                handleChange={(e) => handleFilterChange('priority', e.value)}
                options={[{ value: '', label: t('All Priorities') }, ...ticketPriorities]}
                placeholder={t("Select Priority")}
                className="w-full"
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <DropdownField
                useFormik={false}
                label={t("Status")}
                value={filters.status}
                handleChange={(e) => handleFilterChange('status', e.value)}
                options={[{ value: '', label: t('All Statuses') }, ...ticketStatuses]}
                placeholder={t("Select Status")}
                className="w-full"
              />
            </div>
          </div>

          {/* Secondary Filters Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            {/* Assignee Filter */}
            <div className="space-y-2">
              <TextInput
                useFormik={false}
                label={t("Assigned To")}
                value={filters.assignee}
                handleChange={(e) => handleFilterChange('assignee', e.target.value)}
                placeholder={t("Filter by assignee name")}
                className="w-full"
              />
            </div>

            {/* Date Range Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {t("Date Range")}
              </label>
              <DatePicker
                useFormik={false}
                name="dateRange"
                value={
                  filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2
                    ? filters.dateRange.map(date => date ? moment(date, 'YYYY-MM-DD').toDate() : null)
                    : null
                }
                onChange={e => handleFilterChange('dateRange', e.value)}
                placeholder={t("Select date range")}
                selectionMode="range"
                dateFormat="dd/mm/yy"
                showButtonBar
                readOnlyInput
                className="w-full"
              />
            </div>

            {/* Reset Button */}
            <div className="flex justify-start">
              <Button
                onClick={handleResetFilters}
                label={t("Reset Filters")}
                icon={<FaRedo />}
                buttonType="secondary"
                className="px-6"
              />
            </div>
          </div>

          {/* Summary Stats */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white p-4 rounded-lg border text-center shadow-sm">
              <div className="text-2xl font-bold text-blue-600">
                {filteredTickets.filter(t => t.status === 'Open').length}
              </div>
              <div className="text-sm text-gray-600">{t("Open")}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border text-center shadow-sm">
              <div className="text-2xl font-bold text-purple-600">
                {filteredTickets.filter(t => t.status === 'InProgress').length}
              </div>
              <div className="text-sm text-gray-600">{t("In Progress")}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border text-center shadow-sm">
              <div className="text-2xl font-bold text-yellow-600">
                {filteredTickets.filter(t => t.status === 'OnHold').length}
              </div>
              <div className="text-sm text-gray-600">{t("On Hold")}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border text-center shadow-sm">
              <div className="text-2xl font-bold text-green-600">
                {filteredTickets.filter(t => t.status === 'Resolved').length}
              </div>
              <div className="text-sm text-gray-600">{t("Resolved")}</div>
            </div>
            <div className="bg-white p-4 rounded-lg border text-center shadow-sm">
              <div className="text-2xl font-bold text-gray-600">{totalRecords}</div>
              <div className="text-sm text-gray-600">{t("Total")}</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="p-6">
          <CustomTable
            data={paginatedTickets}
            columns={enhancedColumns}
            isLoading={false}
            emptyMessage={t("No tickets found")}
          />
          
          {totalRecords > 0 && (
            <CustomPagination
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              totalRecords={totalRecords}
              totalPages={Math.ceil(totalRecords / rowsPerPage)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      {/* Ticket Detail Modal */}
      <ReusableModal
        isVisible={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={`${t("Ticket Details")} - ${selectedTicket?.ticketId}`}
        HeaderIcon={FaTicketAlt}
        className="w-[90vw] max-w-[1200px]"
      >
        {selectedTicket && (
          <TicketDetail
            ticket={selectedTicket}
            onClose={() => setIsDetailModalOpen(false)}
            onUpdate={handleUpdateTicket}
          />
        )}
      </ReusableModal>
    </div>
  );
}